// JARVIS Notification Service — Browser notifications for trading events

const PREFS_KEY = 'jarvis_notification_prefs';
const ALERTS_KEY = 'jarvis_price_alerts';

// ─── Preferences ─────────────────────────────────────────────────────────────
export const getNotifPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREFS_KEY) || 'null') || defaultPrefs(); }
  catch { return defaultPrefs(); }
};

const defaultPrefs = () => ({
  enabled: false,
  sessionAlerts: true,     // London/NY session start notifications
  recapReminder: true,     // 8 PM IST daily recap reminder
  riskAlerts: true,        // Daily loss limit warnings
  priceAlerts: true,       // Custom price level alerts
  reminderHour: 20,        // Hour (IST) for daily recap reminder
});

export const saveNotifPrefs = (prefs) =>
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));

// ─── Price Alerts ─────────────────────────────────────────────────────────────
export const getPriceAlerts = () => {
  try { return JSON.parse(localStorage.getItem(ALERTS_KEY) || '[]'); }
  catch { return []; }
};

export const savePriceAlert = (alert) => {
  const list = getPriceAlerts();
  const entry = {
    id:        `PA${Date.now()}`,
    price:     parseFloat(alert.price),
    direction: alert.direction || 'both',  // 'above' | 'below' | 'both'
    label:     alert.label || '',
    active:    true,
    triggered: false,
    created_at: new Date().toISOString(),
  };
  localStorage.setItem(ALERTS_KEY, JSON.stringify([...list, entry]));
  return entry;
};

export const deletePriceAlert = (id) =>
  localStorage.setItem(ALERTS_KEY, JSON.stringify(getPriceAlerts().filter(a => a.id !== id)));

export const markAlertTriggered = (id) => {
  const list = getPriceAlerts().map(a => a.id === id ? { ...a, triggered: true, active: false } : a);
  localStorage.setItem(ALERTS_KEY, JSON.stringify(list));
};

// ─── Permission ───────────────────────────────────────────────────────────────
export const requestPermission = async () => {
  if (!('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  const result = await Notification.requestPermission();
  return result;
};

export const getPermissionStatus = () => {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
};

// ─── Send Notification ────────────────────────────────────────────────────────
export const sendNotification = (title, body, options = {}) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(title, {
      body,
      icon:   '/logo192.png',
      badge:  '/favicon.ico',
      silent: false,
      tag:    options.tag || 'jarvis',
      ...options,
    });
  } catch {}
};

// ─── Session Alert Checker ────────────────────────────────────────────────────
// Call this on a tick (every minute) to check if a session is starting
const SESSION_ALERTS = [
  { hour: 9,  minute: 0,  name: 'London Session',   emoji: '🇬🇧', quality: 'PRIME'    },
  { hour: 14, minute: 0,  name: 'London + NY Overlap',emoji: '🌍', quality: 'PRIME'   },
  { hour: 8,  minute: 30, name: 'Pre-London Setup',  emoji: '⏰', quality: 'PREPARE'  },
  { hour: 20, minute: 0,  name: 'Daily Recap Time',  emoji: '📚', quality: 'REMINDER' },
];

let lastAlertMinute = -1;

export const checkSessionAlerts = (prefs) => {
  if (!prefs?.enabled || !prefs?.sessionAlerts) return;
  const now   = new Date();
  const istOffset = 5.5 * 60; // IST = UTC+5:30
  const ist   = new Date(now.getTime() + (istOffset - now.getTimezoneOffset()) * 60000);
  const h     = ist.getHours();
  const m     = ist.getMinutes();
  const key   = h * 60 + m;

  if (key === lastAlertMinute) return;

  SESSION_ALERTS.forEach(alert => {
    if (alert.hour === h && alert.minute === m) {
      lastAlertMinute = key;
      if (alert.quality === 'REMINDER' && !prefs.recapReminder) return;

      sendNotification(
        `${alert.emoji} ${alert.name} ${alert.quality === 'REMINDER' ? '— Time to Log' : 'Starting'}`,
        alert.quality === 'REMINDER'
          ? 'JARVIS: Sir, time to complete your daily recap and teach me what happened today.'
          : `JARVIS: ${alert.name} is starting. Check your setups and be ready.`,
        { tag: `session-${alert.name}` }
      );
    }
  });
};

// ─── Price Alert Checker ──────────────────────────────────────────────────────
let lastCheckedPrice = 0;

export const checkPriceAlerts = (currentPrice, prefs) => {
  if (!prefs?.enabled || !prefs?.priceAlerts || !currentPrice) return;
  if (Math.abs(currentPrice - lastCheckedPrice) < 0.5) return; // debounce

  const alerts = getPriceAlerts().filter(a => a.active && !a.triggered);
  alerts.forEach(alert => {
    const prev = lastCheckedPrice;
    const curr = currentPrice;
    const hit  =
      (alert.direction === 'above' && prev < alert.price && curr >= alert.price) ||
      (alert.direction === 'below' && prev > alert.price && curr <= alert.price) ||
      (alert.direction === 'both'  && ((prev < alert.price && curr >= alert.price) || (prev > alert.price && curr <= alert.price)));

    if (hit) {
      sendNotification(
        `🎯 Price Alert — XAU/USD ${curr.toFixed(2)}`,
        `${alert.label || 'Target reached'}: Price hit $${alert.price}`,
        { tag: `price-${alert.id}` }
      );
      markAlertTriggered(alert.id);
    }
  });

  lastCheckedPrice = currentPrice;
};
